import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

export interface GanttJob {
  id: number;
  job_number: string;
  title: string;
  client: string;
  status: string;
  start_date: string | null;
  end_date: string | null;
}

interface GanttChartProps {
  jobs: GanttJob[];
}

const STATUS_COLOR: Record<string, string> = {
  in_progress: '#3b82f6',
  scheduled: '#C77D3F',
  quoted: '#D69E2E',
  completed: '#5C7F4F',
  cancelled: '#9ca3af',
  on_hold: '#6b7280',
};

const STATUS_LABEL: Record<string, string> = {
  in_progress: 'In Progress',
  scheduled: 'Scheduled',
  quoted: 'Quoted',
  completed: 'Completed',
  cancelled: 'Cancelled',
  on_hold: 'On Hold',
};

function addDays(date: Date, days: number) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function toDate(s: string | null): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

export const GanttChart = ({ jobs }: GanttChartProps) => {
  const [tooltip, setTooltip] = useState<{ job: GanttJob; x: number; y: number } | null>(null);

  // Build a 14-week window: 2 weeks back, 12 weeks forward
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const windowStart = useMemo(() => addDays(today, -14), [today]);
  const windowEnd = useMemo(() => addDays(today, 84), [today]);
  const totalDays = Math.round((windowEnd.getTime() - windowStart.getTime()) / 86400000);

  // Only show jobs that overlap with the window and have at least a start date
  const visibleJobs = useMemo(
    () =>
      jobs.filter((j) => {
        const s = toDate(j.start_date);
        const e = toDate(j.end_date);
        if (!s) return false;
        const jobEnd = e ?? addDays(s, 7);
        return jobEnd >= windowStart && s <= windowEnd;
      }),
    [jobs, windowStart, windowEnd]
  );

  // Week markers
  const weeks = useMemo(() => {
    const marks: { label: string; dayOffset: number }[] = [];
    let d = new Date(windowStart);
    // align to Monday
    const day = d.getDay();
    const diff = day === 0 ? 1 : day === 1 ? 0 : 8 - day;
    d = addDays(d, diff);
    while (d <= windowEnd) {
      const offset = Math.round((d.getTime() - windowStart.getTime()) / 86400000);
      const label = d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
      marks.push({ label, dayOffset: offset });
      d = addDays(d, 7);
    }
    return marks;
  }, [windowStart, windowEnd]);

  // SVG dimensions
  const ROW_H = 36;
  const HEADER_H = 32;
  const LEFT_W = 200;
  const RIGHT_PAD = 16;
  const SVG_W = 800;
  const CHART_W = SVG_W - LEFT_W - RIGHT_PAD;
  const SVG_H = HEADER_H + visibleJobs.length * ROW_H + 8;

  const dayX = (date: Date) =>
    LEFT_W + ((date.getTime() - windowStart.getTime()) / 86400000 / totalDays) * CHART_W;

  const todayX = dayX(today);

  if (visibleJobs.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <span className="material-symbols-outlined text-4xl text-gray-300">bar_chart</span>
        <p className="text-sm text-gray-500">No jobs with scheduled dates in the current window</p>
        <Link to="/jobs/new" className="text-sm font-bold text-primary hover:underline">Add a job</Link>
      </div>
    );
  }

  return (
    <div className="relative overflow-x-auto">
      {/* Legend */}
      <div className="flex flex-wrap gap-4 mb-3 text-xs text-gray-500">
        {Object.entries(STATUS_LABEL).map(([k, v]) => (
          <span key={k} className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ background: STATUS_COLOR[k] || '#9ca3af' }} />
            {v}
          </span>
        ))}
      </div>

      <svg
        viewBox={`0 0 ${SVG_W} ${SVG_H}`}
        className="w-full"
        style={{ minWidth: '600px', height: `${SVG_H}px` }}
        onMouseLeave={() => setTooltip(null)}
      >
        {/* Header row */}
        <rect x={0} y={0} width={SVG_W} height={HEADER_H} fill="#f9fafb" />
        <line x1={LEFT_W} y1={0} x2={LEFT_W} y2={SVG_H} stroke="#e5e7eb" strokeWidth={1} />

        {/* Week labels */}
        {weeks.map((w) => {
          const x = LEFT_W + (w.dayOffset / totalDays) * CHART_W;
          return (
            <g key={w.dayOffset}>
              <line x1={x} y1={HEADER_H} x2={x} y2={SVG_H} stroke="#f3f4f6" strokeWidth={1} />
              <text x={x + 4} y={HEADER_H - 8} fontSize={9} fill="#9ca3af">{w.label}</text>
            </g>
          );
        })}

        {/* Today line */}
        {todayX >= LEFT_W && todayX <= SVG_W - RIGHT_PAD && (
          <g>
            <line x1={todayX} y1={HEADER_H} x2={todayX} y2={SVG_H} stroke="#C77D3F" strokeWidth={1.5} strokeDasharray="4 2" />
            <text x={todayX + 3} y={HEADER_H - 4} fontSize={9} fill="#C77D3F" fontWeight="bold">Today</text>
          </g>
        )}

        {/* Job rows */}
        {visibleJobs.map((job, i) => {
          const y = HEADER_H + i * ROW_H;
          const s = toDate(job.start_date)!;
          const eRaw = toDate(job.end_date);
          const e = eRaw ?? addDays(s, 7);
          const clampedS = s < windowStart ? windowStart : s;
          const clampedE = e > windowEnd ? windowEnd : e;
          const bx = dayX(clampedS);
          const bw = Math.max(4, dayX(clampedE) - bx);
          const color = STATUS_COLOR[job.status] || '#9ca3af';
          const isEven = i % 2 === 0;

          return (
            <g key={job.id}>
              {/* Row background */}
              <rect x={0} y={y} width={SVG_W} height={ROW_H} fill={isEven ? '#ffffff' : '#f9fafb'} />

              {/* Job name label */}
              <text x={8} y={y + ROW_H / 2 + 4} fontSize={11} fill="#374151" className="cursor-default">
                {job.title.length > 22 ? job.title.slice(0, 22) + '…' : job.title}
              </text>

              {/* Bar */}
              <rect
                x={bx} y={y + 8} width={bw} height={ROW_H - 16}
                rx={3} fill={color} opacity={0.85}
                className="cursor-pointer"
                onMouseEnter={(ev) => {
                  const svgEl = (ev.target as SVGElement).closest('svg')!;
                  const rect = svgEl.getBoundingClientRect();
                  setTooltip({ job, x: ev.clientX - rect.left, y: ev.clientY - rect.top - 60 });
                }}
              />

              {/* Bar label (if wide enough) */}
              {bw > 40 && (
                <text x={bx + 6} y={y + ROW_H / 2 + 4} fontSize={10} fill="white" fontWeight="600">
                  {job.job_number}
                </text>
              )}

              {/* Row border */}
              <line x1={0} y1={y + ROW_H} x2={SVG_W} y2={y + ROW_H} stroke="#f3f4f6" strokeWidth={1} />
            </g>
          );
        })}
      </svg>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="absolute z-20 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 pointer-events-none shadow-lg max-w-[220px]"
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          <p className="font-bold truncate">{tooltip.job.title}</p>
          <p className="text-gray-300">{tooltip.job.client}</p>
          <p className="text-gray-400 mt-0.5">
            {tooltip.job.start_date ?? '?'} → {tooltip.job.end_date ?? 'TBD'}
          </p>
          <p className="mt-0.5" style={{ color: STATUS_COLOR[tooltip.job.status] || '#9ca3af' }}>
            {STATUS_LABEL[tooltip.job.status] || tooltip.job.status}
          </p>
        </div>
      )}
    </div>
  );
};
